import { Component, DestroyRef, HostListener, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactInformationsService } from '../../Services/contact-informations.service';
import { Router, ActivatedRoute } from '@angular/router';
import { ProjectTheme } from "src/app/Models/ProjectTheme";
import { LINKS } from 'admin-common';
import { CategoryService } from '../../Services/category.service';
import { Category } from 'src/app/Models/category';
import { Brands } from 'admin-common';
import { LayoutTexts, LayoutTextsService } from '../../Services/layout-texts.service';

/** "À propos de nous" links; `key` is the label under `layout.footer.aboutUs`. */
const ABOUT_US_LINKS = [
  { key: 'contactUs', link: '/contact' },
  { key: 'help', link: '/assistance' },
];

/** "Informations" links; `key` is the label under `layout.footer.usefulLinks`. */
const USEFUL_LINKS = [
  { key: 'generalConditions', link: '/generalconditions' },
  { key: 'privacy', link: '/privacy' },
  { key: 'legalNotice', link: '/mentionsLegales' },
];

/**
  Composant Footer de l'application
 */
@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent implements OnInit {

  brands: Brands[] = [{
    id: 1,
    name: 'Canon',
    imageUrl: "assets/images/brands/acer.svg"
  },
  {
    id: 10,
    name: 'Apple',
    imageUrl: "assets/images/brands/apple.svg"
  },
  {
    id: 2,
    name: 'Acer',
    imageUrl: "assets/images/brands/acer.svg"
  },
  {
    id: 3,
    name: 'WiPrint',
    imageUrl: "assets/images/brands/wiprint.png"
  },
  {
    id: 4,
    name: 'Dell',
    imageUrl: "assets/images/brands/dell.svg"
  },
  {
    id: 5,
    name: 'HP',
    imageUrl: "assets/images/brands/hp.svg"
  },
  {
    id: 6,
    name: 'Intel',
    imageUrl: "assets/images/brands/intel.png"
  },
  {
    id: 7,
    name: 'Lenovo',
    imageUrl: "assets/images/brands/lenovo.svg"
  },
  {
    id: 8,
    name: 'NVIDIA',
    imageUrl: "assets/images/brands/nvidia.svg"
  },
  {
    id: 9,
    name: 'WiPrint',
    imageUrl: "assets/images/brands/wiprint.svg"
  },

  ];
  // Footer inputs
  /** Couleur de base de la banque */
  baseColor: 'green-500' | 'blue-500' | '' = 'blue-500'; // Changed to blue for ABC Bank
  /** Logo de la banque */
  baseLogo: string = 'wulogo.png';
  bankLogo: string = 'wulogo.png';
  /** Description de la banque */
  bankDescription: string = '';
  /** Thème de la banque */
  theme: ProjectTheme = 'WUnivers';
  /** Textes du bloc d'appel à l'action du footer */
  cta = { badge: '', title: '', description: '', primary: '', secondary: '' };
  /** Liens "à propos de nous" (libellés dans la langue courante) */
  aboutUsLinks: LINKS[] = [];
  /** Liens "Informations" (libellés dans la langue courante) */
  usefullLinksList: LINKS[] = [];
  /** Liens "suivez-nous" (`key` : nom du réseau sous `layout.footer.socialMedia`, affiché dans la langue courante) */
  socialMediaLinks = [
    {
      socialMediaIcon: 'facebook.svg',
      socialMediaName: '',
      key: 'facebook',
      socialMediaLink: 'https://www.facebook.com/wissal.store/',
    },
    {
      socialMediaIcon: 'Instagram.svg',
      socialMediaName: '',
      key: 'instagram',
      socialMediaLink: 'https://www.instagram.com/wissalunivers/',
    },
    {
      socialMediaIcon: 'Linkedin.svg',
      socialMediaName: '',
      key: 'linkedin',
      socialMediaLink: 'https://www.linkedin.com/company/wissal-univers/',
    },
    {
      socialMediaIcon: 'Tiktok.svg',
      socialMediaName: '',
      key: 'tiktok',
      socialMediaLink: 'https://www.tiktok.com/@wissalunivers',
    },
  ];

  /** Liste des Produits ( catégories principales / niveau 1 ) */
  majorCategories: LINKS[] = [];
  /** Formulaire de contact */
  form!: FormGroup;
  /** Année courante */
  currentYear = new Date().getFullYear();
  /** Informations de contact */
  //contactInformations!: ContactInformations;
  /** URL de base */
  baseUrl = '';
  /**copyright year */
  //copyRightYear = "2025"
  moreProductsLink: string = '/products';
  storesLink: string = '/stores';

  private readonly destroyRef = inject(DestroyRef);
  /**
   * Constructeur du composant
   * @param fb - FormBuilder pour les formulaires
   * @param contactInformationService - Service pour les infos de contact
   * @param dialog - Service MatDialog pour les popups
   * @param router - Router Angular
   * @param translationService - Service de traduction
   * @param route - ActivatedRoute pour la route active
   * @param categoryService - Service pour les catégories produits
   * @param layoutTexts - Textes du footer dans la langue courante
   */
  constructor(
    private fb: FormBuilder,
    private contactInformationService: ContactInformationsService,
    // private dialog: MatDialog,
    private router: Router,
    //private route: ActivatedRoute,
    private categoryService: CategoryService,
    layoutTexts: LayoutTextsService
  ) {
    layoutTexts.layout$
      .pipe(takeUntilDestroyed())
      .subscribe((layout) => this.applyTranslations(layout));
  }

  /** Applique la langue courante aux textes passés aux composants de la librairie. */
  private applyTranslations(layout: LayoutTexts): void {
    const footer = layout.footer;

    this.bankDescription = footer['bankDescription'];
    this.aboutUsLinks = ABOUT_US_LINKS.map(({ key, link }) => ({ label: footer['aboutUs'][key], link }));
    this.usefullLinksList = USEFUL_LINKS.map(({ key, link }) => ({ label: footer['usefulLinks'][key], link }));
    this.cta = footer['cta'];
    this.socialMediaLinks = this.socialMediaLinks.map((item) => ({
      ...item,
      socialMediaName: footer['socialMedia']?.[item.key] ?? '',
    }));
  }

  /**
   * Méthode appelée à l'initialisation du composant
   */
  ngOnInit(): void {
    //base url of route
    this.baseUrl = location.origin;
    //**  this.contactInformationService.ContactInformation$.subscribe((change) => {
    //**   this.contactInformations! = change;
    //**     });*/

    this.form = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
    });

    this.loadMajorCategories();
  }

  /**
   * Ouvre le popup "Dossier à fournir"
   * @param enterAnimationDuration Durée de l’animation d’entrée
   * @param exitAnimationDuration Durée de l’animation de sortie
   */
  goToFolderPopup(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ) {
    /*  this.dialog.open(FilesSecondPopupComponent, {
        width: '460px',
        enterAnimationDuration,
        exitAnimationDuration,
        panelClass: 'no-padding-dialog',
      });*/
  }
  /**
   * Redirige vers une page utile en fonction du type
   * @param target Type de lien utile
   */
  navigateTo(target: string) {
    switch (target) {
      case 'GENERAL_CONDITIONS':
        this.router.navigate(['/useful-links'], {
          queryParams: {
            type: target,
          },
        });
        break;
      case 'CONFIDENTIALITY':
        this.router.navigate(['/privacy']);
        break;
      case 'LEGAL_NOTICE':
        this.router.navigate(['/useful-links'], {
          queryParams: {
            type: target,
          },
        });
        break;
      case 'CLIENT_FOLDER':
        this.router.navigate(['/client-folder'], {
          queryParams: {
            type: target,
          },
        });
        break;
      case 'ELIGIBILITY_CONDITIONS':
        this.router.navigate(['/eligibility-conditions']);
        break;
    }
  }

  /** Redirige vers la page d’accueil.*/
  goToHome() {
    this.router.navigate(['/']);
  }

  /**
   * Charge les catégories principales (niveau 1) depuis le service.
   */
  loadMajorCategories(): void {
    // categories are re-published by the header on each language change
    this.categoryService.categories
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories: Category[]) => {
        this.majorCategories = categories
          .filter((cat) => cat.parentId === null)
          .map((cat) => ({
            label: this.toTitleCase(cat.label),
            link: this.createHref(cat.label, cat.id!),
          }));
      });
  }
  /**
   * Crée un lien de navigation vers une catégorie.
   * @param categoryName Nom de la catégorie
   * @param categoryId ID de la catégorie
   * @returns URL complète vers la catégorie
   */
  createHref(categoryName: string, categoryId: number): string {
    const sanitizedCatName = categoryName
      .replace(/[^a-zA-Z0-9]/g, '-')
      .toUpperCase();
    return `/products/category/${categoryId}/${sanitizedCatName}?categories=&inStock=true`;
  }

  toTitleCase(str: string) {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
